import moment from "moment";
import React, { useState } from "react";
import { Card, CardBody, Row, Col, Offcanvas, OffcanvasHeader, OffcanvasBody, FormGroup, Label, Input } from "reactstrap";
import Spinner from "../../../common/spinner/spinner";
import { AdminService } from "../../../services/AdminService";
import Select from "react-select";

// Import StarRating component
interface StarRatingProps {
  rate: number;
}

const StarRating: React.FC<StarRatingProps> = ({ rate }) => {
  const stars = [];

  for (let i = 0; i < rate; i++) {
    stars.push(
      <i
        key={i}
        className="fa fa-star"
        aria-hidden="true"
        style={{
          color: "#F4C542",
          cursor: "pointer",
          fontSize: "15px",
        }}
      ></i>
    );
  }

  return <>{stars}</>;
};

interface SessionFeedbackTabProps {
  // Props needed from parent component
  initialFeedbacks?: any[];
}

const SessionFeedbackTab: React.FC<SessionFeedbackTabProps> = ({ initialFeedbacks = [] }) => {
  const [sessionFeedback, setSessionFeedback] = useState<any[]>(initialFeedbacks);
  const [userCount, setUserCount] = useState(20);
  const [offset, setOffset] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Filter states
  const [isFilterOptions, setFilterOptions] = useState(false);
  const [isSearchableString, setIsSearchableString] = useState("");
  const [selectedTherapist, setSelectedTherapist] = useState("");
  const [therapistOptions, setTherapistOptions] = useState<any[]>([]);

  const LIMIT = 20;

  React.useEffect(() => {
    getAllFeedbacks();
    loadTherapistOptions();
  }, []);

  const loadTherapistOptions = () => {
    // Load therapist options for filter dropdown
    AdminService.getAllTherapistsSimple().then(res => {
      if (res.success && res.data) {
        const options = res.data.map((therapist: any) => ({
          value: therapist._id,
          label: therapist.name
        }));
        setTherapistOptions([{ value: "", label: "All Therapists" }, ...options]);
      }
    });
  };

  const getAllFeedbacks = () => {
    setIsLoading(true);
    const filterData = {
      searchableString: isSearchableString,
      therapistId: selectedTherapist
    };
    AdminService.getAllSessionFeedbacks(LIMIT, offset, filterData).then(res => {
      if(res.success){
        setSessionFeedback(res.data);
        setUserCount(res.data.length);
        setIsLoading(false);
      } else {
        setSessionFeedback([]);
        setIsLoading(false)
      }
    });
  };

  const seeMore = () => {
    const updatedSkip = offset + 1;
    setOffset(updatedSkip);
    setIsLoading(true);
    const filterData = {
      searchableString: isSearchableString,
      therapistId: selectedTherapist
    };
    AdminService.getAllSessionFeedbacks(LIMIT, updatedSkip, filterData).then(res => {
      if (res.success) {
        res.data.map((feedback: any) => {
          setSessionFeedback(feedbacks => [...feedbacks, feedback]);
        });
        setUserCount(res.data.length);
      }
      setIsLoading(false);
    });
  };

  const toggleFilter = () => {
    setFilterOptions(!isFilterOptions);
  };

  const handleTherapistChange = (selectedOption: any) => {
    setSelectedTherapist(selectedOption ? selectedOption.value : "");
  };

  const filterFeedbacks = () => {
    setOffset(1);
    getAllFeedbacks();
    toggleFilter();
  };

  const resetFilter = () => {
    setIsSearchableString("");
    setSelectedTherapist("");
    setOffset(1);
    // Call getAllFeedbacks without filter data
    setIsLoading(true);
    AdminService.getAllSessionFeedbacks(LIMIT, 1).then(res => {
      if(res.success){
        setSessionFeedback(res.data);
        setUserCount(res.data.length);
        setIsLoading(false);
      } else {
        setSessionFeedback([]);
        setIsLoading(false)
      }
    });
    toggleFilter();
  };



  return (
    <>
      <Card className="m-h">
        <CardBody>
          <Row>
            <Col className="d-flex admin-filter">
              <button className="btn btn-sm mr-2" onClick={toggleFilter}>
                Filter <i className="bx bx-filter-alt"></i>
              </button>
            </Col>
          </Row>
          <Row>
            <Col sm="12" className="mt10">
            {sessionFeedback !== undefined && sessionFeedback.length > 0 ? (
              <>
                <div className="table-responsive">
                  <table className="table">
                    <thead className="thead-dark">
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Created By</th>
                        <th scope="col">Created Date</th>
                        <th scope="col" style={{maxWidth: "200px"}}>Feedback</th>
                        <th scope="col">Client</th>
                        <th scope="col">Therapist</th>
                        <th scope="col">Session Start Time</th>
                        <th scope="col">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionFeedback &&
                        sessionFeedback.map((feedback: any, index: number) => (
                          <tr key={feedback?._id}>
                            <th scope="row">{index + 1}</th>
                            <td>
                                {feedback?.createdByDetails[0]?.firstname} {feedback?.createdByDetails[0]?.lastname}
                            </td>
                            <td>{moment(feedback?.createdAt).format("YYYY-MM-DD hh:mm A")}</td>
                            <td style={{ maxWidth: "400px"}}>{feedback?.feedback}</td>
                            <td style={{minWidth: "120px"}}>
                              {feedback?.clientDetails?.firstname} {feedback?.clientDetails?.lastname}
                            </td>
                            <td style={{minWidth: "120px"}}>
                              {feedback?.therapistDetails?.firstname} {feedback?.therapistDetails?.lastname}
                            </td>
                            <td style={{minWidth: "120px"}}>
                              {moment(feedback?.meetingDetails?.createdAt).format("YYYY-MM-DD hh:mm A")}
                            </td>
                            <td style={{minWidth: "120px"}}>
                              <StarRating rate={feedback?.rate} />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {userCount >= LIMIT && (
                  <>
                    <button className="btn btn-warning text-center mt-4" onClick={seeMore}>
                      See More
                    </button>
                  </>
                )}
                {userCount == 0 && <span>Feedbacks not found....</span>}
              </>
            ) : (
              <>
                {isLoading ? <Spinner /> : <div>Feedbacks not found.</div>}</>
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>

    {/* Filter Offcanvas */}
    <Offcanvas className="offcanvas-end2" isOpen={isFilterOptions} direction="end" toggle={toggleFilter}>
      <OffcanvasHeader toggle={toggleFilter}>Filter Session Feedback</OffcanvasHeader>
      <OffcanvasBody>
        <Row className="mt-4">
          <Col lg={12}>
            <FormGroup>
              <Label>Search by Client Name, Email or Phone</Label>
              <Input
                id="search"
                name="text"
                placeholder="Search Users (ex: first/last name, email or phone)"
                type="text"
                onKeyDown={(e) => e.key === 'Enter' && filterFeedbacks()}
                onChange={e => setIsSearchableString(e.target.value)}
                value={isSearchableString}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col lg={12}>
            <FormGroup>
              <Label>Search by Therapist Name</Label>
              <Select
                id="therapistName"
                options={therapistOptions}
                onChange={handleTherapistChange}
                value={therapistOptions.find(option => option.value === selectedTherapist)}
                placeholder="Select Therapist"
                isClearable
              />
            </FormGroup>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col lg={6}>
            <button
              className={`btn ${isLoading ? "btn-primary" : "btn-success"} w-100`}
              onClick={filterFeedbacks}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Search"}
            </button>
          </Col>
          <Col lg={6}>
            <button
              className="btn btn-secondary w-100"
              onClick={resetFilter}
              disabled={isLoading}
            >
              Reset
            </button>
          </Col>
        </Row>
      </OffcanvasBody>
    </Offcanvas>
  </>
  );
};

export default SessionFeedbackTab;
